import { useState } from "react";
import { Helmet } from "react-helmet";
import Icon from "../../components/AppIcon";
import Header from "../../components/ui/Header";
import AvailabilityIndicator from "./components/AvailabilityIndicator";
import ContactForm from "./components/ContactForm";
import ContactMethodCard from "./components/ContactMethodCard";
import QuickContactActions from "./components/QuickContactActions";
import RecentActivityFeed from "./components/RecentActivityFeed";
import SocialMediaCard from "./components/SocialMediaCard";
import {
  AvailabilityStatus,
  ContactFormData,
  ContactMethod,
  InquiryType,
  RecentActivity,
  SocialPlatform,
} from "./types";

const Contact = () => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const contactMethods: ContactMethod[] = [
    {
      id: "whatsapp",
      name: "WhatsApp",
      icon: "MessageCircle",
      description: "Quick mobile chat for immediate discussions and collaboration ideas",
      responseTime: "Within 1 hour",
      link: "https://wa.me/qr/MIPMGLGF4RZ5O1",
      isPrimary: true,
    },
    {
      id: "email",
      name: "Email",
      icon: "Mail",
      description: "Professional correspondence for detailed project proposals and formal inquiries",
      responseTime: "Within 24 hours",
      link: "mailto:muhammad@fotih.world",
      isPrimary: false,
    },
    {
      id: "linkedin",
      name: "LinkedIn",
      icon: "Linkedin",
      description: "Professional networking for career opportunities and industry connections",
      responseTime: "Within 48 hours",
      link: "https://www.linkedin.com/in/fotih-abdullaev-901817356",
      isPrimary: false,
    },
    {
      id: "github",
      name: "GitHub",
      icon: "Github",
      description: "Code collaboration, open source contributions, and technical discussions",
      responseTime: "Within 72 hours",
      link: "https://github.com/Muhammadninja?tab=repositories/Muhammadninja",
      isPrimary: false,
    },
  ];

  const socialPlatforms: SocialPlatform[] = [
    {
      id: "github",
      name: "GitHub",
      icon: "Github",
      username: "Muhammadninja",
      link: "https://github.com/Muhammadninja?tab=repositories/Muhammadninja",
      color: "#6e5494",
    },
    {
      id: "linkedin",
      name: "LinkedIn",
      icon: "Linkedin",
      username: "Muhammad Fotih Abdullaev",
      link: "https://www.linkedin.com/in/fotih-abdullaev-901817356/",
      color: "#0077b5",
    },
    {
      id: "twitter",
      name: "Twitter",
      icon: "Twitter",
      username: "mfotih_dev",
      link: "https://x.com/muhammad640961?s=21",
      color: "#1da1f2",
    },
    {
      id: "instagram",
      name: "Instagram",
      icon: "Instagram",
      username: "mgk_206",
      link: "https://www.instagram.com/mgk_206?igsh=MTA3OXN3dHMwMHA2aA%3D%3D&utm_source=qr",
      color: "#e4405f",
    },
  ];

  const inquiryTypes: InquiryType[] = [
    {
      value: "business",
      label: "Business Collaboration",
      description: "Partnership opportunities, project proposals, and business ventures",
    },
    {
      value: "academic",
      label: "Academic Networking",
      description: "Study groups, course projects, and peer learning opportunities",
    },
    {
      value: "recruitment",
      label: "Recruitment & Career",
      description: "Job opportunities, internships, and career discussions",
    },
    {
      value: "mentorship",
      label: "Mentorship",
      description: "Guidance requests, advice seeking, and professional development",
    },
    {
      value: "technical",
      label: "Technical Discussion",
      description: "Code reviews, technical questions, and development insights",
    },
    {
      value: "other",
      label: "Other",
      description: "General inquiries and other topics",
    },
  ];

  const availabilityStatus: AvailabilityStatus = {
    status: "available",
    message: "Currently available for new collaborations and exciting projects!",
    bestTimes: [
      "Monday - Friday: 9:00 AM - 6:00 PM EST",
      "Weekends: 10:00 AM - 4:00 PM EST",
      "Emergency contacts: Available via WhatsApp 24/7",
    ],
  };

  const recentActivities: RecentActivity[] = [
    {
      id: "1",
      platform: "github",
      content: "Pushed new commits to react-portfolio-template repository with enhanced TypeScript support",
      timestamp: new Date(Date.now() - 3600000),
      link: "https://github.com/Muhammadninja?tab=repositories/Muhammadninja/react-portfolio-template",
    },
    {
      id: "2",
      platform: "linkedin",
      content: "Shared insights on modern web development practices and React 18 features",
      timestamp: new Date(Date.now() - 7200000),
      link: "https://www.linkedin.com/in/fotih-abdullaev-901817356",
    },
    {
      id: "3",
      platform: "twitter",
      content: "Discussing the future of web development and AI integration in modern applications",
      timestamp: new Date(Date.now() - 172800000),
      link: "https://x.com/muhammad640961?s=21/mfotih_dev",
    },
  ];

  const quickActions = [
    {
      id: "whatsapp",
      label: "WhatsApp Chat",
      icon: "MessageCircle",
      action: () => window.open("https://wa.me/qr/MIPMGLGF4RZ5O1", "_blank"),
      variant: "default" as const,
    },
    {
      id: "email",
      label: "Send Email",
      icon: "Mail",
      action: () => window.location.href = "mailto:muhammad@fotih.world",
      variant: "outline" as const,
    },
    {
      id: "linkedin",
      label: "LinkedIn Connect",
      icon: "Linkedin",
      action: () => window.open("https://www.linkedin.com/in/fotih-abdullaev-901817356/", "_blank"),
      variant: "outline" as const,
    },
    {
      id: "schedule",
      label: "Schedule Call",
      icon: "Calendar",
      action: () => window.open("https://calendly.com/muhammad-fotih", "_blank"),
      variant: "secondary" as const,
    },
  ];

  const handleContactMethodSelect = (method: ContactMethod) => {
    window.open(method.link, "_blank", "noopener,noreferrer");
  };

  const handleFormSubmit = async (_data: ContactFormData) => {
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 5000);
  };

  return (
    <>
      <Helmet>
        <title>Contact - Muhammad Fotih Abdullaev | Let's Connect & Collaborate</title>
        <meta
          name="description"
          content="Get in touch with Muhammad Fotih Abdullaev for business collaborations, academic networking, or career opportunities. Multiple contact methods including WhatsApp, email, and social media."
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-24 pb-16 px-4 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                <Icon name="MessageSquare" size={16} />
                <span>Let's Connect</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4">
                Get in Touch
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Whether you have a project idea, collaboration opportunity, or just want to say hello,
                I'm always excited to connect with fellow developers, entrepreneurs, and innovators.
              </p>
            </div>

            {showSuccessMessage && (
              <div className="mb-8 p-4 rounded-lg bg-success/10 border border-success/20 flex items-center gap-3 max-w-4xl mx-auto">
                <Icon name="CheckCircle2" size={24} color="var(--color-success)" />
                <div>
                  <p className="text-success font-medium">Message sent successfully!</p>
                  <p className="text-sm text-muted-foreground">
                    I'll get back to you as soon as possible.
                  </p>
                </div>
              </div>
            )}

            <div className="mb-12">
              <QuickContactActions actions={quickActions} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-6">
                    Choose Your Preferred Contact Method
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {contactMethods.map((method) => (
                      <ContactMethodCard
                        key={method.id}
                        method={method}
                        onSelect={handleContactMethodSelect}
                      />
                    ))}
                  </div>
                </div>

                <div className="p-8 rounded-lg border border-border bg-surface">
                  <div className="flex items-center gap-3 mb-6">
                    <Icon name="Send" size={24} color="var(--color-primary)" />
                    <h2 className="text-2xl font-bold text-foreground">
                      Send Me a Message
                    </h2>
                  </div>
                  <ContactForm
                    inquiryTypes={inquiryTypes}
                    onSubmit={handleFormSubmit}
                  />
                </div>
              </div>

              <div className="space-y-8">
                <AvailabilityIndicator status={availabilityStatus} />

                <div className="p-6 rounded-lg border border-border bg-surface">
                  <RecentActivityFeed activities={recentActivities} />
                </div>

                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Share2" size={20} />
                    Connect on Social Media
                  </h3>
                  <div className="grid grid-cols-1 gap-4">
                    {socialPlatforms.map((platform) => (
                      <SocialMediaCard key={platform.id} platform={platform} />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 rounded-lg border border-border bg-surface text-center">
              <Icon name="MapPin" size={32} color="var(--color-primary)" className="mx-auto mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-2">Location</h3>
              <p className="text-muted-foreground mb-4">
                Orlando, Florida, USA
                <br />
                Valencia College Student
              </p>
              <p className="text-sm text-muted-foreground">
                Available for remote collaborations worldwide and local meetups in the Orlando area
              </p>
            </div>
          </div>
        </main>

        <footer className="border-t border-border bg-surface py-8 px-4 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Muhammad Fotih Abdullaev. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Contact;